import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import HeroSection from '@/components/HeroSection';
import SectionHeading from '@/components/SectionHeading';
import Gallery from '@/components/Gallery';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const VidezLesa = () => {
  const galleryImages = [
    { 
      src: 'https://images.unsplash.com/photo-1581922814484-0b48460b7010?q=80&w=2070', 
      alt: 'Keramične ploščice videz lesa' 
    },
    { 
      src: 'https://images.unsplash.com/photo-1609799408354-199f85d5ca58?q=80&w=2070', 
      alt: 'Keramične ploščice videz lesa v kopalnici' 
    },
    { 
      src: 'https://images.unsplash.com/photo-1614632538395-6224c54be48b?q=80&w=2070', 
      alt: 'Keramične ploščice videz lesa v dnevni sobi' 
    },
    { 
      src: 'https://images.unsplash.com/photo-1588854337115-1c67d9247e4d?q=80&w=2070', 
      alt: 'Keramične ploščice videz lesa v hodniku' 
    },
    { 
      src: 'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?q=80&w=2070', 
      alt: 'Keramične ploščice videz lesa v kuhinji' 
    },
  ];

  const specs = [
    { name: 'Dimenzije', value: '20x120 cm, 30x120 cm' },
    { name: 'Material', value: 'Keramika' },
    { name: 'Odpornost', value: 'Visoka proti obrabi, madežem in zmrzali' },
    { name: 'Uporaba', value: 'Notranji in zunanji prostori' },
    { name: 'Vzdrževanje', value: 'Enostavno čiščenje' },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <main className="pt-16">
        <HeroSection 
          title="Keramične ploščice z videzom lesa"
          subtitle="Topel videz lesa z vzdržljivostjo keramike"
          imageSrc="https://images.unsplash.com/photo-1581922814484-0b48460b7010?q=80&w=2070"
          primaryButtonText="Zahtevaj ponudbo"
          primaryButtonLink="/kontakt"
        />

        <section className="py-16 bg-stone-50">
          <div className="container-custom">
            <SectionHeading 
              title="Videz lesa"
              subtitle="Toplina in estetika lesa s trpežnostjo in praktičnostjo keramike"
            />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="prose max-w-none">
                  <p className="text-lg text-stone-700 mb-4">
                    Keramične ploščice z videzom lesa predstavljajo popolno kombinacijo naravne lepote lesa in praktičnosti keramike. Zaradi napredne tehnologije tiska je videz izjemno realističen, s teksturo in vzorci, ki posnemajo pravi les.
                  </p>
                  <p className="text-lg text-stone-700 mb-4">
                    Na voljo so v različnih odtenkih in stilih, od svetlega hrasta do temnega oreha, kar omogoča ustvarjanje toplega in prijetnega ambienta v vašem domu.
                  </p>
                  <p className="text-lg text-stone-700 mb-8">
                    V nasprotju s pravim lesom so te ploščice odporne proti praskam, madežem, vlagi in ne zahtevajo posebnega vzdrževanja. Primerne so za vse prostore, vključno s kopalnico in kuhinjo, kjer je prisotna vlaga.
                  </p>
                  
                  <h3 className="text-xl font-semibold text-stone-800 mb-4">Tehnične specifikacije</h3>
                  <ul className="space-y-2">
                    {specs.map((spec, index) => (
                      <li key={index} className="flex justify-between items-center border-b border-stone-200 py-2">
                        <span className="font-medium text-stone-800">{spec.name}:</span>
                        <span className="text-stone-600">{spec.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mt-8">
                  <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white font-semibold" asChild>
                    <a href="/kontakt" className="flex items-center gap-2">
                      Zahtevaj ponudbo <ArrowRight size={16} />
                    </a>
                  </Button>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Gallery images={galleryImages.slice(0, 3)} />
              </motion.div>
            </div>
          </div>
        </section>
        
        <section className="py-16">
          <div className="container-custom">
            <SectionHeading 
              title="Galerija"
              subtitle="Primeri postavitev keramičnih ploščic z videzom lesa"
            />
            
            <div className="mt-12">
              <Gallery images={galleryImages} />
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-lg text-stone-700 mb-6 max-w-3xl mx-auto">
                Za več informacij o keramičnih ploščicah z videzom lesa ali za naročilo nas kontaktirajte. Z veseljem vam bomo pomagali izbrati najboljšo rešitev za vaš prostor.
              </p>
              <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white font-semibold" asChild>
                <a href="/kontakt" className="flex items-center gap-2">
                  Zahtevaj ponudbo <ArrowRight size={16} />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default VidezLesa;